import React, { useEffect, useRef } from 'react';
import { FaArrowRight, FaPlay } from 'react-icons/fa';
import { trackOrderStart, trackButtonClick, trackSectionView } from '../utils/gtm';
import { safeBase64Encode } from '../utils/safeEncode';

const Hero = () => {
  const ref = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // GTM: Track hero section view
            trackSectionView('Hero');
            
            const reveals = entry.target.querySelectorAll('.reveal');
            reveals.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('show');
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      io.observe(ref.current);
    }

    return () => io.disconnect();
  }, []);

  const handleImageError = (e) => {
    if (e.target.dataset.fallback === 'true') {
      const parent = e.target.parentElement;
      parent.innerHTML = `
        <div style="
          width: 100%; 
          height: 100%; 
          background: linear-gradient(135deg, #FF7A3D, #FFB14B); 
          display: grid; 
          place-items: center; 
          color: white; 
          font-weight: 800; 
          font-size: clamp(1rem, 4vw, 1.5rem);
          font-family: 'Sora', sans-serif;
          text-align: center;
          padding: 1rem;
        ">
          🍛 Mealzy App
        </div>
      `;
      return;
    }

    e.target.dataset.fallback = 'true';
    
    const svgContent = `
      <svg width="400" height="600" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FF7A3D;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#FFB14B;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grad)"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
              fill="white" font-size="24" font-weight="bold" font-family="Sora, sans-serif">
          🍛 Mealzy App
        </text>
      </svg>
    `;
    
    const encodedSvg = safeBase64Encode(svgContent);
    if (encodedSvg) {
      e.target.src = `data:image/svg+xml;base64,${encodedSvg}`;
    } else {
      // Ultimate fallback - use URL encoding
      e.target.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
    }
  };

  // GTM: Track order button click
  const handleOrderClick = () => {
    trackOrderStart();
    trackButtonClick('Order Now', 'Hero Section');
  };

  // GTM: Track demo button click  
  const handleDemoClick = () => {
    trackButtonClick('Watch Demo', 'Hero Section');
  };

  return (
    <section id="home" className="section hero-section">
      <div className="container" ref={ref}>
        <div className="hero-grid">
          {/* Content */}
          <div className="hero-content">
            <div className="badge reveal">
              🏆 Pune's fastest growing delivery
            </div>

            <h1 className="hero-title reveal">
              Your Everyday <span className="text-gradient">Food & Veggie</span>{' '}
              Delivery App
            </h1>

            <p className="hero-description reveal">
              Mealzy Foods is your go‑to app for fresh, delicious, and wholesome
              meals delivered right to your doorstep. From comforting home‑style
              meals and quick snacks to farm‑fresh vegetables — fast, fresh, and
              flavorful.
            </p>

            <div className="hero-buttons reveal">
              <a
                className="btn btn-primary"
                href="https://play.google.com/store/apps/details?id=com.prashantsableannapurnadosaj.app&hl=en"
                target="_blank"
                rel="noreferrer"
                onClick={handleOrderClick}
              >
                <FaArrowRight /> Order now
              </a>
              <button 
                className="btn btn-ghost"
                onClick={handleDemoClick}
              >
                <FaPlay /> Watch demo
              </button>
            </div>

            <div className="hero-stats reveal">
              <span>10k+ customers</span>
              <span className="stats-divider">•</span>
              <span>500+ daily orders</span>
              <span className="stats-divider">•</span>
              <span>4.8★ rating</span>
            </div>
          </div>

          {/* Image */}
          <div className="hero-image reveal">
            <div className="phone-container">
              <div className="card float phone-mockup">
                <div className="phone-screen">
                  <img
                    src="https://play-lh.googleusercontent.com/lHfImrWLeDofPStOTj5zQXKeKywdLA__YrlwLDas9MOJFqAVOfu_qSJPn5i1Jfokm_w=w2560-h1440-rw"
                    alt="app preview"
                    onError={handleImageError}
                  />
                  <div className="phone-overlay" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          padding-top: clamp(6rem, 15vh, 9rem);
          padding-bottom: clamp(3rem, 8vh, 5rem);
          position: relative;
          overflow: hidden;
        }
        
        .hero-section::before {
          content: '';
          position: absolute;
          inset: -40px;
          z-index: -1;
          background: radial-gradient(1200px 500px at 10% 0%, rgba(255,122,61,.15), transparent 60%), 
                      radial-gradient(900px 500px at 100% 40%, rgba(255,177,75,.12), transparent 55%);
          opacity: 0.8;
        }

        @media (max-width: 768px) {
          .hero-section {
            overflow-x: hidden !important;
          }
          
          .hero-section::before {
            inset: 0;
          }
        }

        .hero-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: center;
        }

        @media (min-width: 768px) {
          .hero-grid {
            grid-template-columns: 1.1fr 0.9fr;
            gap: 4rem;
          }
        }

        .hero-content {
          text-align: center;
        }

        @media (min-width: 768px) {
          .hero-content {
            text-align: left;
          }
        }

        .hero-title {
          font-size: clamp(2.25rem, 8vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          margin: 1.5rem 0;
        }

        .text-gradient {
          background: var(--grad);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: clamp(1rem, 3vw, 1.25rem);
          color: var(--muted);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
          align-items: center;
        }

        @media (min-width: 640px) {
          .hero-buttons {
            flex-direction: row;
            gap: 1.5rem;
          }
        }

        @media (min-width: 768px) {
          .hero-buttons {
            justify-content: flex-start;
          }
        }

        .hero-stats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
          color: var(--muted);
          font-weight: 600;
          font-size: clamp(0.8rem, 2vw, 0.9rem);
        }

        @media (min-width: 768px) {
          .hero-stats {
            justify-content: flex-start;
          }
        }

        .stats-divider {
          display: none;
        }

        @media (min-width: 640px) {
          .stats-divider {
            display: inline;
          }
        }

        .phone-container {
          display: flex;
          justify-content: center;
        }

        .phone-mockup {
          width: 100%;
          max-width: 280px;
          aspect-ratio: 9/16;
          border-radius: 2rem;
          padding: 1rem;
          background: linear-gradient(180deg, #181822, #111118);
          border: 1px solid var(--glass-bd);
        }

        @media (min-width: 640px) {
          .phone-mockup {
            max-width: 320px;
            border-radius: 2.5rem;
            padding: 1.25rem;
          }
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          border-radius: 1.5rem;
          overflow: hidden;
          position: relative;
        }

        @media (min-width: 640px) {
          .phone-screen {
            border-radius: 2rem;
          }
        }

        .phone-screen img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .phone-overlay {
          position: absolute;
          background: linear-gradient(180deg, transparent, rgba(0,0,0,.55));
        }
      `}</style>
    </section>
  );
};

export default Hero;
